import moment, { unitOfTime } from "moment";
import { DateType, DateValueType } from "react-tailwindcss-datepicker";
import type { TaskResult } from "./types";
import { ListFieldsRecordType } from "src/api/config";
import { myTaskCheckListResultsListName } from "src/api/config/meetings.config";

/** Must match checklist datepicker minDate — inclusive on the earliest day. */
export const CHECKLIST_EDITABLE_DAYS_BACK = 7;

/** Local shift boundary: before this hour the previous calendar day is the current shift. */
export const CHECKLIST_SHIFT_START_HOUR = 8;

const PERIOD_TYPE_MAP: Record<string, unitOfTime.StartOf> = {
  Неделя: "isoWeek",
  Месяц: "month",
  Квартал: "quarter",
  Год: "year",
};

const PERIOD_ADD_UNIT: Record<string, moment.unitOfTime.DurationConstructor> = {
  day: "day",
  isoWeek: "week",
  week: "week",
  month: "month",
  quarter: "quarter",
  year: "year",
};

function applyShiftStartHour(day: moment.Moment): moment.Moment {
  return day.clone().hour(CHECKLIST_SHIFT_START_HOUR).minute(0).second(0).millisecond(0);
}

function getShiftDayEndFromStart(shiftPeriodStart: moment.Moment): moment.Moment {
  return shiftPeriodStart.clone().add(1, "day").subtract(1, "millisecond");
}

/** Shift day label from local time: before 08:00 → yesterday, from 08:00 → today. */
export function getChecklistShiftDayDate(referenceDate?: DateType): Date {
  const local = referenceDate ? moment(referenceDate) : moment();

  if (!local.isValid()) {
    return moment().startOf("day").toDate();
  }

  if (local.hour() < CHECKLIST_SHIFT_START_HOUR) {
    return local.clone().subtract(1, "day").startOf("day").toDate();
  }

  return local.clone().startOf("day").toDate();
}

export function getChecklistDefaultPeriodDate(referenceDate?: DateType): DateValueType {
  const shiftDay = moment(getChecklistShiftDayDate(referenceDate)).startOf("day");

  return {
    startDate: shiftDay.toDate(),
    endDate: shiftDay.clone().endOf("day").toDate(),
  };
}

export function getShiftDayRange(searchDate: DateType): { start: Date; end: Date } {
  const start = applyShiftStartHour(moment(searchDate).startOf("day"));

  return {
    start: start.toDate(),
    end: getShiftDayEndFromStart(start).toDate(),
  };
}

export function getShiftPeriodReference(searchDate: DateType): moment.Moment {
  return applyShiftStartHour(moment(searchDate).startOf("day"));
}

export function isShiftPeriodReferenceInRange(
  referenceDate: DateType,
  start: moment.Moment,
  end: moment.Moment,
): boolean {
  return getShiftPeriodReference(referenceDate).isBetween(start, end, undefined, "[]");
}

export function getChecklistEarliestEditableDate(): Date {
  return moment(getChecklistShiftDayDate())
    .startOf("day")
    .subtract(CHECKLIST_EDITABLE_DAYS_BACK, "days")
    .toDate();
}

export function isChecklistDateBeforeEditableWindow(targetDate: DateType | null | undefined): boolean {
  if (!targetDate || !moment(targetDate).isValid()) {
    return false;
  }

  const earliestEditableDay = moment(getChecklistEarliestEditableDate()).startOf("day");
  return moment(targetDate).startOf("day").isBefore(earliestEditableDay);
}

export function getStartAndEndDateByType(date: moment.MomentInput, type: unitOfTime.StartOf) {
  const local = moment(date);
  const periodStart = applyShiftStartHour(local.clone().startOf(type));
  const addUnit = PERIOD_ADD_UNIT[type as string] ?? "day";
  const nextPeriodStart = applyShiftStartHour(local.clone().startOf(type).add(1, addUnit));
  const periodEnd = nextPeriodStart.clone().subtract(1, "millisecond");

  return { start: periodStart, end: periodEnd };
}

function getReturnObjectDateByType(date: moment.MomentInput, type: unitOfTime.StartOf) {
  const { start, end } = getStartAndEndDateByType(date, type);
  const referenceDate = moment(date);

  if (!referenceDate.isValid() || !isShiftPeriodReferenceInRange(referenceDate.toDate(), start, end)) {
    return { start: null, end: null };
  }

  return {
    start: start.toDate(),
    end: end.toDate(),
  };
}

export function getStartEndDateByPeriod(period: string, searchDate: DateType | undefined) {
  const defaultRange = getShiftDayRange(getChecklistShiftDayDate());

  if (!searchDate || !moment(searchDate).isValid()) {
    return defaultRange;
  }

  const periodType = PERIOD_TYPE_MAP[period];

  if (periodType) {
    const range = getReturnObjectDateByType(searchDate, periodType);

    if (range.start && range.end) {
      return range as { start: Date; end: Date };
    }

    return defaultRange;
  }

  return getShiftDayRange(searchDate);
}

export function getResultByTaskStatus(
  taskResultItem: ListFieldsRecordType[typeof myTaskCheckListResultsListName] | undefined
): TaskResult {
  const taskResult: TaskResult = { isDone: false, hasDeviation: false };

  if (taskResultItem) {
    if (taskResultItem.TaskStatus === "Оба") {
      taskResult.isDone = true;
      taskResult.hasDeviation = true;
    } else if (taskResultItem.TaskStatus === "Выполнено") {
      taskResult.isDone = true;
    } else if (taskResultItem.TaskStatus === "Отклонение") {
      taskResult.hasDeviation = true;
    }
  }

  return taskResult;
}
